import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-duotone-svg-icons'
import { Course, ExternalCourse } from 'graphqlAPI'
import {
  createRouteMonitoringCourse,
  createRouteMonitoringExternalCourse,
} from 'enum/routes'
import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import { prefix } from '../../constants'
import { ProgressEvaluation } from '../../components'
import Options from '../Options'
import Progress from '../CourseProgress'
import EvaluationProgressPopover from '../../../../EvaluationProgressPopover'
import styles from './useColumns.module.scss'

type Data = Course | ExternalCourse
export function useColumns() {
  const { t } = useTranslation()
  type Column = {
    title: string
    key:
      | keyof Data
      | 'advance_finish'
      | 'trim_name'
      | 'progress_evaluations_approved'
      | 'action'
      | 'synchronousCourse'
    sorteable?: boolean
    width?: number
    hideable?: boolean
    position?: string
    render?: (...data: any) => any
  }

  const prefixHeaders = `${prefix}.headers`
  const prefixColumns = `${prefix}.columns`

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixHeaders}.nameCourse`),
        key: 'trim_name',
        sorteable: true,
        hideable: false,
        render: (_, { id, name, __typename, integrationCourseLink }: any) => {
          const isExternalCourse = __typename === 'ExternalCourse'
          return (
            <div className={styles.name}>
              <Link
                to={
                  isExternalCourse
                    ? createRouteMonitoringExternalCourse({ id: `${id}` })
                    : createRouteMonitoringCourse({ id: `${id}` })
                }
                className={styles.link}
              >
                {name}
              </Link>
              <ExternalCourseIndicator
                origin="TABLE"
                url={integrationCourseLink}
              />
            </div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.type`),
        key: '__typename',
        width: 150,
        sorteable: true,
        render: (value) => <span>{t(`${prefixColumns}.types.${value}`)}</span>,
      },
      {
        title: t(`${prefixHeaders}.totalParticipants`),
        key: 'participantsCount',
        width: 170,
        sorteable: true,
        render: (value) => (
          <span>{t(`${prefixColumns}.participants`, { count: value })}</span>
        ),
      },
      {
        title: t(`${prefixHeaders}.synchronous`),
        key: 'synchronousCourse',
        width: 85,
        render: (synchronousCourse: boolean) =>
          synchronousCourse ? (
            <FontAwesomeIcon
              icon={faCheck}
              style={{ color: '#36b37e', display: 'flex', margin: 'auto' }}
            />
          ) : null,
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: 'advance_finish',
        width: 120,
        sorteable: true,
        render: (_, value?: Data) =>
          (value?.progress && (
            <Progress id={'progress-' + value.id} progress={value.progress} />
          )) ||
          '---',
      },
      {
        title: t(`${prefixHeaders}.progressEvaluations`),
        key: 'progress_evaluations_approved',
        width: 120,
        sorteable: true,
        render: (_, value?: Data) => {
          const progress = value?.progressEvaluations
          const participantsCount = value?.participantsCount
          return (
            (progress?.total && participantsCount && (
              <EvaluationProgressPopover
                progress={{
                  approved: progress.approved,
                  reproved: progress.reproved,
                }}
                total={participantsCount * progress.total}
              >
                <ProgressEvaluation
                  progress={progress}
                  total={participantsCount * progress.total}
                  text={(percentages) => (
                    <>
                      {t(`${prefixColumns}.progressEvaluation`, {
                        count: percentages.approved,
                      })}
                    </>
                  )}
                />
              </EvaluationProgressPopover>
            )) || <div className="text-center">---</div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.hours`),
        key: 'hours',
        width: 140,
        sorteable: true,
        render: (data) => (
          <span>
            {t(`${prefixColumns}.hoursTotal`, {
              count: +(data || 0).toFixed(2),
            })}
          </span>
        ),
      },
      {
        title: t(`${prefixHeaders}.actions`),
        key: 'action',
        width: 100,
        hideable: false,
        position: 'left',
        render: (id, course) => <Options course={course} />,
      },
    ],
    [prefixColumns, prefixHeaders, t]
  )
  return columns
}
